import React, { useEffect, useMemo, useRef, useState } from "react";
import "./rich-comment-input.css";
import {
  buildAttachmentMarkup,
  extractMentionMetadataFromHtml,
  extractPlainTextFromHtml,
  normalizeCommentHtml,
  stripMentionMarkupFromHtml,
} from "./commentContent";

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function getMentionQuery(text = "") {
  const match = text.match(/@([^\s@]*)$/);
  return match ? match[1] : null;
}

function getCaretRangeInEditor(editor) {
  if (!editor) return null;
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (!editor.contains(range.startContainer) || !editor.contains(range.endContainer)) return null;
  return range;
}

function getTextBeforeCaret(editor, range) {
  if (!editor || !range) return "";
  const clone = range.cloneRange();
  clone.selectNodeContents(editor);
  clone.setEnd(range.endContainer, range.endOffset);
  return clone.toString();
}

function getMentionReplacementRange(editor, range, query) {
  if (!editor || !range) return null;
  const beforeText = getTextBeforeCaret(editor, range);
  const trigger = `@${query ?? ""}`;
  const startIndex = beforeText.lastIndexOf(trigger);
  if (startIndex === -1) return null;
  const endIndex = startIndex + trigger.length;

  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
  let currentIndex = 0;
  let startNode = null;
  let startOffset = 0;
  let endNode = null;
  let endOffset = 0;

  while (walker.nextNode()) {
    const node = walker.currentNode;
    const nextIndex = currentIndex + (node.textContent || "").length;
    if (!startNode && startIndex >= currentIndex && startIndex <= nextIndex) {
      startNode = node;
      startOffset = startIndex - currentIndex;
    }
    if (!endNode && endIndex >= currentIndex && endIndex <= nextIndex) {
      endNode = node;
      endOffset = endIndex - currentIndex;
      break;
    }
    currentIndex = nextIndex;
  }

  if (!startNode || !endNode) return null;
  const mentionRange = document.createRange();
  mentionRange.setStart(startNode, startOffset);
  mentionRange.setEnd(endNode, endOffset);
  return mentionRange;
}

function wrapSelectionWithStyle(styleName, styleValue) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return false;
  const range = selection.getRangeAt(0);
  const span = document.createElement("span");
  span.style[styleName] = styleValue;
  try {
    range.surroundContents(span);
  } catch (error) {
    const content = range.extractContents();
    span.appendChild(content);
    range.insertNode(span);
  }
  selection.removeAllRanges();
  const nextRange = document.createRange();
  nextRange.selectNodeContents(span);
  nextRange.collapse(false);
  selection.addRange(nextRange);
  return true;
}

export default function RichCommentInput({
  value,
  onChange,
  onSubmit,
  users = [],
  teams = [],
  mentionScopeLabel = "item",
  placeholder = "Write a comment…",
  rows = 3,
  submitLabel = "Post",
  className = "",
  collapsible = false,
}) {
  const [expanded, setExpanded] = useState(!collapsible);
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [savedSelection, setSavedSelection] = useState(null);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [mentionMenuStyle, setMentionMenuStyle] = useState(null);
  const [fontSize, setFontSize] = useState("13px");
  const [fontColor, setFontColor] = useState("#dce8f8");
  const [attachments, setAttachments] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [toolbarState, setToolbarState] = useState({
    paragraph: false,
    bold: false,
    italic: false,
    underline: false,
    strikeThrough: false,
    unorderedList: false,
    orderedList: false,
  });

  useEffect(() => {
    if (!editorRef.current) return;
    const normalized = normalizeCommentHtml(value);
    if (editorRef.current.innerHTML !== normalized) {
      editorRef.current.innerHTML = normalized;
    }
  }, [value]);

  function emitChange(nextHtml, nextAttachments = attachments) {
    const payload = `${nextHtml}${buildAttachmentMarkup(nextAttachments)}`;
    onChange(payload);
  }

  function syncToolbarState(range = getCaretRangeInEditor(editorRef.current)) {
    const editor = editorRef.current;
    if (!editor || !range) {
      setToolbarState({
        paragraph: false,
        bold: false,
        italic: false,
        underline: false,
        strikeThrough: false,
        unorderedList: false,
        orderedList: false,
      });
      return;
    }

    const resolveState = (predicate) => {
      let node = range.startContainer.nodeType === Node.TEXT_NODE ? range.startContainer.parentNode : range.startContainer;
      while (node && node !== editor) {
        if (predicate(node)) return true;
        node = node.parentNode;
      }
      return false;
    };

    const computedSize = window.getComputedStyle(range.startContainer.nodeType === Node.TEXT_NODE ? range.startContainer.parentElement : range.startContainer).fontSize;
    const computedColor = window.getComputedStyle(range.startContainer.nodeType === Node.TEXT_NODE ? range.startContainer.parentElement : range.startContainer).color;
    setFontSize(computedSize || "13px");
    setFontColor((() => {
      const match = (computedColor || "").match(/\d+/g);
      if (!match || match.length < 3) return "#dce8f8";
      return `#${match.slice(0, 3).map((part) => Number(part).toString(16).padStart(2, "0")).join("")}`;
    })());

    setToolbarState({
      paragraph: !resolveState((node) => ["UL", "OL"].includes(node.tagName)),
      bold: resolveState((node) => ["B", "STRONG"].includes(node.tagName) || Number(node.style?.fontWeight) >= 600 || node.style?.fontWeight === "bold"),
      italic: resolveState((node) => ["I", "EM"].includes(node.tagName) || node.style?.fontStyle === "italic"),
      underline: resolveState((node) => node.tagName === "U" || (node.style?.textDecoration || "").includes("underline")),
      strikeThrough: resolveState((node) => ["S", "STRIKE"].includes(node.tagName) || (node.style?.textDecoration || "").includes("line-through")),
      unorderedList: resolveState((node) => node.tagName === "UL"),
      orderedList: resolveState((node) => node.tagName === "OL"),
    });
  }

  function syncMentionUi(range = getCaretRangeInEditor(editorRef.current)) {
    const editor = editorRef.current;
    if (!editor || !range) {
      setMentionQuery(null);
      setMentionMenuStyle(null);
      return;
    }
    const nextQuery = getMentionQuery(getTextBeforeCaret(editor, range));
    setMentionQuery(nextQuery);
    if (nextQuery === null) {
      setMentionMenuStyle(null);
      return;
    }
    const wrapRect = editor.parentElement?.getBoundingClientRect();
    const rect = range.getBoundingClientRect();
    if (!wrapRect) return;
    const top = Math.max(0, rect.bottom - wrapRect.top + 8);
    const left = Math.max(0, Math.min(rect.left - wrapRect.left, wrapRect.width - 240));
    setMentionMenuStyle({ top: `${top}px`, left: `${left}px` });
  }

  function syncFromEditor() {
    const editor = editorRef.current;
    if (!editor) return;
    const attachmentBlock = editor.querySelector(".rc-comment-attachments");
    if (attachmentBlock) attachmentBlock.remove();
    setMentions(extractMentionMetadataFromHtml(editor.innerHTML));
    emitChange(editor.innerHTML);
    const range = getCaretRangeInEditor(editor);
    if (range) {
      setSavedSelection(range.cloneRange());
    }
    syncMentionUi(range);
    syncToolbarState(range);
  }

  function applyCommand(command, commandValue = null) {
    editorRef.current?.focus();
    document.execCommand("styleWithCSS", false, true);
    document.execCommand(command, false, commandValue);
    syncFromEditor();
  }

  function applyFontSize(nextSize) {
    setFontSize(nextSize);
    editorRef.current?.focus();
    if (!wrapSelectionWithStyle("fontSize", nextSize)) return;
    syncFromEditor();
  }

  function applyFontColor(nextColor) {
    setFontColor(nextColor);
    editorRef.current?.focus();
    if (!wrapSelectionWithStyle("color", nextColor)) {
      document.execCommand("foreColor", false, nextColor);
    }
    syncFromEditor();
  }

  function restoreSelection() {
    const selection = window.getSelection();
    if (!selection || !savedSelection) return;
    selection.removeAllRanges();
    selection.addRange(savedSelection);
  }

  function insertMention(entity) {
    editorRef.current?.focus();
    restoreSelection();
    const editor = editorRef.current;
    const range = getCaretRangeInEditor(editor);
    if (!range) return;
    const mentionRange = getMentionReplacementRange(editor, range, mentionQuery);
    const targetRange = mentionRange || range;
    const selection = window.getSelection();
    const mentionNode = document.createElement("span");
    mentionNode.className = "rc-comment-mention";
    mentionNode.setAttribute("data-mention-id", entity.id);
    mentionNode.setAttribute("data-mention-name", entity.name || "");
    mentionNode.setAttribute("data-mention-type", entity.mentionType || "user");
    mentionNode.setAttribute("contenteditable", "false");
    mentionNode.setAttribute("spellcheck", "false");
    mentionNode.textContent = `@${entity.name}`;
    targetRange.deleteContents();
    targetRange.insertNode(mentionNode);
    targetRange.setStartAfter(mentionNode);
    targetRange.collapse(true);
    const spacer = document.createTextNode(" ");
    targetRange.insertNode(spacer);
    targetRange.setStartAfter(spacer);
    targetRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(targetRange);
    setMentionQuery(null);
    setMentionMenuStyle(null);
    setSelectedMentionIndex(0);
    syncFromEditor();
  }

  function handleFilePick(event) {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    Promise.all(files.map((file) => (
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve({
          id: `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          fileName: file.name,
          fileUrl: reader.result,
        });
        reader.onerror = reject;
        reader.readAsDataURL(file);
      })
    ))).then((resolved) => {
      const nextAttachments = [...attachments, ...resolved];
      setAttachments(nextAttachments);
      emitChange(editorRef.current?.innerHTML || "", nextAttachments);
    }).catch(() => {});
    event.target.value = "";
  }

  function insertPlainText(text) {
    editorRef.current?.focus();
    restoreSelection();
    const selection = window.getSelection();
    const range = getCaretRangeInEditor(editorRef.current);
    if (!selection || !range) return;
    const node = document.createTextNode(text);
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    syncFromEditor();
  }

  function handleRemoveAttachment(id) {
    const nextAttachments = attachments.filter((attachment) => attachment.id !== id);
    setAttachments(nextAttachments);
    emitChange(editorRef.current?.innerHTML || "", nextAttachments);
  }

  const mentionSections = useMemo(() => {
    if (mentionQuery === null) return [];
    const pattern = new RegExp(escapeRegExp(mentionQuery), "i");
    const people = users
      .filter((user) => pattern.test(user.name || ""))
      .slice(0, 8)
      .map((user) => ({
        id: user.id,
        name: user.name,
        sublabel: user.role ? String(user.role).replace(/_/g, " ").toLowerCase() : "",
        mentionType: "user",
        kind: "person",
      }));
    const teamScopeItems = [
      { id: "__board_scope__", name: "Everyone on this board", mentionType: "scope", kind: "team" },
      { id: "__item_scope__", name: `Everyone on this ${mentionScopeLabel}`, mentionType: "scope", kind: "team" },
    ];
    const teamItems = teams
      .filter((team) => pattern.test(team.name || "") || pattern.test(team.stageLabel || ""))
      .slice(0, 6)
      .map((team) => ({
        id: team.id,
        name: team.name,
        sublabel: team.stageLabel || "",
        mentionType: "team",
        kind: "team",
      }));
    const filteredTeams = [...teamScopeItems, ...teamItems]
      .filter((item) => pattern.test(item.name || "") || pattern.test(item.sublabel || ""));

    return [
      { key: "people", label: "People", items: people },
      { key: "teams", label: "Teams", items: filteredTeams },
    ].filter((section) => section.items.length > 0);
  }, [mentionQuery, mentionScopeLabel, teams, users]);

  const mentionOptions = useMemo(
    () => mentionSections.flatMap((section) => section.items),
    [mentionSections],
  );

  useEffect(() => {
    setSelectedMentionIndex(0);
  }, [mentionQuery]);

  const plainText = extractPlainTextFromHtml(value);
  const isEmpty = !plainText.trim() && attachments.length === 0;

  useEffect(() => {
    if (typeof document === "undefined") return;
    const doc = document.implementation.createHTMLDocument("");
    doc.body.innerHTML = normalizeCommentHtml(value);
    const nextAttachments = Array.from(doc.body.querySelectorAll("[data-rc-attachment='true']")).map((link, index) => ({
      id: link.getAttribute("data-attachment-id") || `embedded-${index}`,
      fileName: link.getAttribute("data-file-name") || link.textContent || "Attachment",
      fileUrl: link.getAttribute("href") || "",
    })).filter((item) => item.fileUrl);
    setAttachments(nextAttachments);
    setMentions(extractMentionMetadataFromHtml(value));
  }, [value]);

  useEffect(() => {
    function handleSelectionChange() {
      const editor = editorRef.current;
      const range = getCaretRangeInEditor(editor);
      if (!range) return;
      setSavedSelection(range.cloneRange());
      syncMentionUi(range);
      syncToolbarState(range);
    }
    document.addEventListener("selectionchange", handleSelectionChange);
    return () => document.removeEventListener("selectionchange", handleSelectionChange);
  }, []);

  async function handleSubmit() {
    const html = normalizeCommentHtml(value);
    if (!extractPlainTextFromHtml(html).trim() && attachments.length === 0) return;
    const cleanHtml = stripMentionMarkupFromHtml(html);
    await onSubmit?.({
      html: cleanHtml,
      text: extractPlainTextFromHtml(cleanHtml),
      attachments,
      mentions: mentions.filter((mention) => mention.type === "user").map((mention) => mention.id),
    });
  }

  // Collapse back when focus leaves the entire component and content is empty
  function handleBlur(event) {
    if (!collapsible) return;
    const next = event.relatedTarget;
    const wrap = event.currentTarget;
    if (wrap && next && wrap.contains(next)) return; // focus staying inside
    if (isEmpty) setExpanded(false);
  }

  if (collapsible && !expanded) {
    return (
      <div
        className={`rc-rich-collapsed-trigger ${className}`}
        onClick={() => setExpanded(true)}
      >
        {placeholder}
      </div>
    );
  }

  return (
    <div className={`rc-rich-comment ${className}`} onBlur={handleBlur} tabIndex={-1}>
      <div className="rc-rich-toolbar">
        <button type="button" className={`rc-rich-tool${toolbarState.paragraph ? " active" : ""}`} onMouseDown={(event) => event.preventDefault()} onClick={() => applyCommand("formatBlock", "p")} title="Paragraph">¶</button>
        <button type="button" className={`rc-rich-tool${toolbarState.bold ? " active" : ""}`} onMouseDown={(event) => event.preventDefault()} onClick={() => applyCommand("bold")} title="Bold"><strong>B</strong></button>
        <button type="button" className={`rc-rich-tool${toolbarState.italic ? " active" : ""}`} onMouseDown={(event) => event.preventDefault()} onClick={() => applyCommand("italic")} title="Italic"><em>I</em></button>
        <button type="button" className={`rc-rich-tool${toolbarState.underline ? " active" : ""}`} onMouseDown={(event) => event.preventDefault()} onClick={() => applyCommand("underline")} title="Underline"><u>U</u></button>
        <button type="button" className={`rc-rich-tool${toolbarState.strikeThrough ? " active" : ""}`} onMouseDown={(event) => event.preventDefault()} onClick={() => applyCommand("strikeThrough")} title="Strike">S</button>
        <select className="rc-rich-select" value={fontSize} onChange={(event) => applyFontSize(event.target.value)} title="Font size">
          <option value="12px">12</option>
          <option value="13px">13</option>
          <option value="14px">14</option>
          <option value="16px">16</option>
          <option value="18px">18</option>
        </select>
        <label className="rc-rich-color" title="Font color">
          <input type="color" value={fontColor} onChange={(event) => applyFontColor(event.target.value)} />
        </label>
        <button type="button" className={`rc-rich-tool${toolbarState.unorderedList ? " active" : ""}`} onMouseDown={(event) => event.preventDefault()} onClick={() => applyCommand("insertUnorderedList")} title="Bullet list">≣</button>
        <button type="button" className={`rc-rich-tool${toolbarState.orderedList ? " active" : ""}`} onMouseDown={(event) => event.preventDefault()} onClick={() => applyCommand("insertOrderedList")} title="Numbered list">≡</button>
        <button type="button" className="rc-rich-tool" onMouseDown={(event) => event.preventDefault()} onClick={() => applyCommand("removeFormat")} title="Clear formatting">×</button>
      </div>

      <div className="rc-rich-editor-wrap">
        {isEmpty && <div className="rc-rich-placeholder" style={{ minHeight: `${rows * 24}px` }}>{placeholder}</div>}
        <div
          ref={editorRef}
          className="rc-rich-editor"
          contentEditable
          suppressContentEditableWarning
          style={{ minHeight: `${rows * 24}px` }}
          onInput={syncFromEditor}
          onBlur={syncFromEditor}
          onKeyUp={syncFromEditor}
          onMouseUp={syncFromEditor}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setMentionQuery(null);
              setMentionMenuStyle(null);
              return;
            }
            if (mentionQuery !== null && mentionOptions.length > 0 && event.key === "ArrowDown") {
              event.preventDefault();
              setSelectedMentionIndex((current) => (current + 1) % mentionOptions.length);
              return;
            }
            if (mentionQuery !== null && mentionOptions.length > 0 && event.key === "ArrowUp") {
              event.preventDefault();
              setSelectedMentionIndex((current) => (current - 1 + mentionOptions.length) % mentionOptions.length);
              return;
            }
            if (mentionQuery !== null && event.key === "Enter" && mentionOptions.length > 0) {
              event.preventDefault();
              insertMention(mentionOptions[selectedMentionIndex] || mentionOptions[0]);
              return;
            }
            if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
              event.preventDefault();
              handleSubmit();
            }
          }}
        />
        {mentionQuery !== null && mentionSections.length > 0 && (
          <div className="rc-mention-dropdown" style={mentionMenuStyle || undefined}>
            {mentionSections.map((section) => (
              <div key={section.key} className="rc-mention-section">
                <div className="rc-mention-section-label">{section.label}</div>
                {section.items.map((item) => {
                  const optionIndex = mentionOptions.findIndex((option) => option.id === item.id);
                  return (
                    <div
                      key={`${section.key}-${item.id}`}
                      className={`rc-mention-item${optionIndex === selectedMentionIndex ? " msel" : ""}`}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        insertMention(item);
                      }}
                    >
                      <span className={`rc-mention-avatar${item.kind === "team" ? " is-team" : ""}`}>
                        {item.kind === "team"
                          ? "◔"
                          : (item.name || "").split(" ").map((part) => part[0]).join("").slice(0, 2).toUpperCase()}
                      </span>
                      <span className="rc-mention-copy">
                        <span className="rc-mention-name">{item.name}</span>
                        {item.sublabel && <span className="rc-mention-sublabel">{item.sublabel}</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rc-rich-utilitybar">
        <button type="button" className="rc-rich-util" onMouseDown={(event) => event.preventDefault()} onClick={() => insertPlainText("@")} title="Mention">@</button>
        <button type="button" className="rc-rich-util" onMouseDown={(event) => event.preventDefault()} onClick={() => fileInputRef.current?.click()} title="Attach file">📎</button>
        <button type="button" className="rc-rich-util rc-rich-util-muted" onMouseDown={(event) => event.preventDefault()} onClick={() => {}} title="GIF">GIF</button>
        <button type="button" className="rc-rich-util rc-rich-util-muted" onMouseDown={(event) => event.preventDefault()} onClick={() => insertPlainText("🙂")} title="Emoji">☺</button>
        <div className="rc-rich-utilityspacer" />
        <input ref={fileInputRef} type="file" multiple style={{ display: "none" }} onChange={handleFilePick} />
      </div>

      {attachments.length > 0 && (
        <div className="rc-rich-attachments">
          {attachments.map((attachment) => (
            <div key={attachment.id} className="rc-rich-attachment-pill">
              <a href={attachment.fileUrl} download={attachment.fileName} target="_blank" rel="noreferrer">{attachment.fileName}</a>
              <button type="button" onClick={() => handleRemoveAttachment(attachment.id)}>✕</button>
            </div>
          ))}
        </div>
      )}

      <div className="rc-rich-actions">
        <button type="button" className="aud-comment-btn" onClick={handleSubmit} disabled={isEmpty}>{submitLabel}</button>
      </div>
    </div>
  );
}
